import { Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useForm, FormProvider } from "react-hook-form";
import {
  generatePath,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getAgreementModalTitle,
  getAgreementModalText,
} from "src/entities/partnerContracts/model/helpers/helpers";
import { useGetPartnerQuery } from "src/shared/api/partner";
import { ModalComponent, EConfirmModalType } from "src/shared/components";
import { ContractBody } from "src/shared/components/contracts/ContractBody";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import {
  useGetPartnerComplexContractQuery,
  usePostAgreementAttachmentMutation,
  usePostAgreementMutation,
  useGetPartnerAgreementTemplateQuery,
} from "../../../model/api/api";
import { agreementDefaultFormValues } from "../../../model/constants/formValues";
import { IAgreementFormValues } from "../../../model/types";
import { AgreementDialogFooter } from "./AgreementDialogFooter";
import { AgreementForm } from "./AgreementForm";

export function NewPartnerAgreement() {
  const { partner_id, contract_id, agreement_id } = useParams();
  const [searchParams] = useSearchParams();
  const shouldLoadAgreementTemplate =
    searchParams.get("template") === "contract" || Boolean(agreement_id);
  const navigate = useNavigate();
  const isIframeRoute = useMatch("/iframe/*");

  const { data: partner } = useGetPartnerQuery(partner_id || skipToken);
  const { data: complexContract } = useGetPartnerComplexContractQuery(
    partner_id && contract_id
      ? {
          partner_id,
          contract_id,
        }
      : skipToken,
  );
  const { data: agreementTemplateText } = useGetPartnerAgreementTemplateQuery(
    shouldLoadAgreementTemplate && partner_id && contract_id
      ? {
          partner_id,
          contract_id,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [postAgreement, { isLoading: isLoadingPost }] =
    usePostAgreementMutation();
  const [postAttachment, { isLoading: isLoadingPostAttachment }] =
    usePostAgreementAttachmentMutation();

  const goBackRoute = generatePath(
    isIframeRoute ? routes.iframePartnerContracts : routes.partnerContractsList,
    { partner_id },
  );
  const handleSaveAgreement = async (formData: IAgreementFormValues) => {
    const { file, data } = formData;
    try {
      const createAgreementResponse = await postAgreement({
        ...data,
        contract_id: Number(contract_id),
        partner_id: Number(partner_id),
        agreement_date: data.agreement_date.format("YYYY-MM-DD"),
      }).unwrap();

      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        const { id } = createAgreementResponse.data;
        await postAttachment({
          agreement_id: String(id),
          partner_id: String(partner_id),
          contract_id: String(contract_id),
          file: formData,
        }).unwrap();
      }
      navigate(goBackRoute);
    } catch (e) {
      showApiError(e);
    }
  };

  const methods = useForm<IAgreementFormValues>({
    mode: "onChange",
    defaultValues: agreementDefaultFormValues,
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(goBackRoute),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });
  const modalTitle =
    complexContract && partner
      ? getAgreementModalTitle({
          partner_name: partner.name,
          contract_number: complexContract.contract_number,
        })
      : "";

  const fallbackModalBody = complexContract
    ? getAgreementModalText({
        contract_date: complexContract.contract_date,
        contract_number: complexContract.contract_number,
      })
    : "";

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      boxProps={{ sx: { height: "90vh" } }}
      disableBodyPadding
      initialBodyShadow
      title={modalTitle}
      onClose={isDirty ? showModal : () => navigate(goBackRoute)}
      open
      modalFooter={
        <AgreementDialogFooter
          isLoading={isLoadingPost || isLoadingPostAttachment}
          onSubmit={handleSubmit(handleSaveAgreement)}
          onCancel={isDirty ? showModal : () => navigate(goBackRoute)}
        />
      }
    >
      {modal}
      <FormProvider {...methods}>
        <ContractBody
          html={agreementTemplateText}
          text={fallbackModalBody}
          form={<AgreementForm />}
        />
      </FormProvider>
    </ModalComponent>
  );
}
